import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Mesh } from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Pause, Play, RotateCcw, StepBack, StepForward } from 'lucide-react';

type TheaterHistoryEntry = {
  timestamp: string;
  role?: 'agent' | 'tool' | 'system';
  lane?: string;
  message: string;
};

type Props = {
  history: TheaterHistoryEntry[];
  cursor: number;
};

function colorForLane(lane: string) {
  if (lane === 'agent1') return '#3b82f6'; // blue-500
  if (lane === 'agent2') return '#d946ef'; // fuchsia-500
  const m = lane.match(/agent(\d+)/i);
  if (!m) return '#94a3b8'; // slate-400
  const idx = Number(m[1]);
  const palette = [
    '#3b82f6', // blue
    '#d946ef', // fuchsia
    '#10b981', // emerald
    '#f59e0b', // amber
    '#06b6d4', // cyan
    '#fb7185', // rose
    '#84cc16', // lime
    '#8b5cf6', // violet
    '#0ea5e9', // sky
    '#f97316', // orange
  ];
  return palette[(idx - 1) % palette.length];
}

function alignForLane(lane: string) {
  if (lane === 'agent2') return 1;
  const m = lane.match(/agent(\d+)/i);
  if (m) {
    const n = Number(m[1]);
    return n % 2 === 0 ? 1 : -1;
  }
  return -1;
}

function agentName(lane: string) {
  if (lane === 'agent1') return 'Agent 1';
  if (lane === 'agent2') return 'Agent 2';
  const m = lane.match(/agent(\d+)/i);
  if (m) return `Agent ${m[1]}`;
  return lane;
}

function hexToRgb(hex: string) {
  const v = hex.replace('#', '').trim();
  const full = v.length === 3 ? v.split('').map((c) => c + c).join('') : v;
  const n = parseInt(full, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
  const clamp = (x: number) => Math.max(0, Math.min(255, Math.round(x)));
  return (
    '#' +
    [clamp(r), clamp(g), clamp(b)]
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('')
  );
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpHex(a: string, b: string, t: number) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex({ r: lerp(A.r, B.r, t), g: lerp(A.g, B.g, t), b: lerp(A.b, B.b, t) });
}

function Scene({
  turns,
  playhead,
}: {
  turns: { lane: string }[];
  playhead: number;
}) {
  const lastIdx = Math.max(0, turns.length - 1);
  const i = Math.max(0, Math.min(lastIdx, Math.floor(playhead)));
  const nextIdx = Math.min(lastIdx, i + 1);
  const frac = nextIdx === i ? 0 : Math.max(0, Math.min(1, playhead - i));

  const a = turns[i];
  const b = turns[nextIdx];

  const ax = alignForLane(a.lane) * 1.55;
  const bx = alignForLane(b.lane) * 1.55;

  // simple arc for the ball
  const x = lerp(ax, bx, frac);
  const y = -0.1 + Math.sin(frac * Math.PI) * 0.55;
  const z = Math.sin(frac * Math.PI) * (i % 2 === 0 ? 0.18 : -0.18);

  const colorA = colorForLane(a.lane);
  const colorB = colorForLane(b.lane);
  const ballColor = lerpHex(colorA, colorB, frac);

  const ballRef = useRef<Mesh | null>(null);

  useFrame((_, dt) => {
    if (!ballRef.current) return;
    ballRef.current.rotation.y += dt * 2.2;
    ballRef.current.rotation.x += dt * 1.4;
  });

  return (
    <group>
      {/* table */}
      <mesh position={[0, -0.6, 0]}>
        <boxGeometry args={[4.6, 0.15, 2.2]} />
        <meshStandardMaterial color="#0b1220" />
      </mesh>

      {/* net */}
      <mesh position={[0, -0.35, 0]}>
        <boxGeometry args={[0.06, 0.6, 2.0]} />
        <meshStandardMaterial color="#334155" />
      </mesh>

      {/* lanes / markers */}
      <mesh position={[-2.0, -0.52, -0.9]}>
        <boxGeometry args={[0.12, 0.02, 1.8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[2.0, -0.52, -0.9]}>
        <boxGeometry args={[0.12, 0.02, 1.8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>

      {/* Agent labels */}
      <Text position={[-2.1, 0.15, 0.9]} fontSize={0.18} color="#93c5fd" anchorX="left">
        Agent (odd)
      </Text>
      <Text position={[2.1, 0.15, 0.9]} fontSize={0.18} color="#f5d0fe" anchorX="right">
        Agent (even)
      </Text>

      {/* Ball */}
      {turns.length ? (
        <mesh ref={ballRef} position={[x, y, z]}>
          <sphereGeometry args={[0.16, 32, 32]} />
          <meshStandardMaterial color={ballColor} emissive={ballColor} emissiveIntensity={0.4} />
        </mesh>
      ) : null}

      {/* Active label */}
      {turns.length ? (
        <Text position={[0, 0.7, 0]} fontSize={0.22} color="#e5e7eb" anchorX="center">
          {agentName(a.lane)} → {agentName(b.lane)}
        </Text>
      ) : (
        <Text position={[0, 0.7, 0]} fontSize={0.22} color="#94a3b8" anchorX="center">
          No agent turns yet
        </Text>
      )}
    </group>
  );
}

export default function AgentPingPong3D({ history, cursor }: Props) {
  const turns = useMemo(() => {
    const visible = history.slice(0, cursor);
    const agentTurns = visible.filter((h) => h.role === 'agent' && h.lane && h.lane !== 'left');
    return agentTurns.map((h) => ({ lane: String(h.lane) }));
  }, [history, cursor]);

  const maxIndex = Math.max(0, turns.length - 1);

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [playhead, setPlayhead] = useState(0);

  useEffect(() => {
    // Clamp playhead if new turns arrive.
    setPlayhead((p) => Math.max(0, Math.min(maxIndex, p)));
  }, [maxIndex]);

  useEffect(() => {
    if (!playing) return;
    if (turns.length <= 1) return;

    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const dt = Math.max(0, (now - last) / 1000);
      last = now;

      setPlayhead((p) => {
        const next = p + dt * speed; // ~ 1 turn per second at 1x
        if (next >= maxIndex) return maxIndex;
        return next;
      });

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, maxIndex, turns.length]);

  useEffect(() => {
    if (playhead >= maxIndex && playing) setPlaying(false);
  }, [playhead, maxIndex, playing]);

  const canPlay = turns.length > 1;

  return (
    <div className="h-full w-full bg-black relative">
      {/* Controls overlay */}
      <div className="absolute left-3 right-3 top-3 z-10 rounded-lg border border-white/10 bg-black/60 backdrop-blur px-3 py-2">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (!canPlay) return;
              setPlaying((p) => !p);
            }}
            disabled={!canPlay}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            <span className="ml-2">{playing ? 'Pause' : 'Play'}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPlaying(false);
              setPlayhead(0);
            }}
            disabled={!turns.length}
            className="border-white/15 text-white hover:bg-white/10"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPlaying(false);
              setPlayhead((p) => Math.max(0, Math.floor(p) - 1));
            }}
            disabled={!turns.length}
            className="border-white/15 text-white hover:bg-white/10"
          >
            <StepBack className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPlaying(false);
              setPlayhead((p) => Math.min(maxIndex, Math.floor(p) + 1));
            }}
            disabled={!turns.length}
            className="border-white/15 text-white hover:bg-white/10"
          >
            <StepForward className="h-4 w-4" />
          </Button>

          <div className="ml-auto flex items-center gap-2 text-xs text-white/70">
            <span>Speed</span>
            <select
              className="h-8 rounded-md bg-black/40 border border-white/15 px-2 text-white"
              value={String(speed)}
              onChange={(e) => setSpeed(Number(e.target.value))}
            >
              <option value="0.5">0.5×</option>
              <option value="1">1×</option>
              <option value="1.5">1.5×</option>
              <option value="2">2×</option>
            </select>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-3">
          <div className="text-xs text-white/60 shrink-0 w-28">
            Turn {turns.length ? Math.min(turns.length, Math.floor(playhead) + 1) : 0}/{turns.length}
          </div>
          <div className="flex-1">
            <Slider
              value={[playhead]}
              min={0}
              max={maxIndex}
              step={0.01}
              onValueChange={([v]) => {
                setPlaying(false);
                setPlayhead(v);
              }}
              disabled={!turns.length}
            />
          </div>
        </div>
      </div>

      <Canvas camera={{ position: [0, 1.8, 4.8], fov: 45 }}>
        <ambientLight intensity={0.45} />
        <directionalLight position={[4, 4, 4]} intensity={1.0} />
        <Scene turns={turns} playhead={playhead} />
        <OrbitControls enablePan={false} minDistance={3.2} maxDistance={7.5} />
      </Canvas>
    </div>
  );
}